// saveCodeUtils.js
import { suggestFilename } from './filenameSuggestions';

/**
 * Prepare code for saving to a file
 * Handles cleanup and special cases
 * 
 * @param {string} code - The code to save
 * @returns {string} - Cleaned code
 */
const prepareCodeForSave = (code) => {
  if (!code) return '';
  
  // Remove any trailing/leading whitespace
  return code.trim();
};

/**
 * Save code to a file using the File System Access API if available
 * Falls back to a traditional download approach if not supported
 * 
 * @param {string} code - The code content to save
 * @param {string} language - The language of the code (for filename suggestion)
 * @param {string} [promptContext] - The prompt that generated the code
 * @returns {Promise<boolean>} - Whether save was successful
 */
export const saveCodeToFile = async (code, language, promptContext = '') => {
  if (!code || !language) {
    console.error('Code or language missing');
    return false;
  }
  
  const preparedCode = prepareCodeForSave(code);
  
  try {
    // Get suggested filename
    const suggestedFilename = await suggestFilename(preparedCode, language, promptContext);
    
    // Create a blob with the content
    const blob = new Blob([preparedCode], { type: 'text/plain' });
    
    // Try to use the File System Access API if available
    if (window.showSaveFilePicker) {
      try {
        const opts = {
          suggestedName: suggestedFilename,
          types: [{
            description: `${language.toUpperCase()} File`,
            accept: { 'text/plain': [`.${getExtensionFromFilename(suggestedFilename)}`] }
          }]
        };
        
        const fileHandle = await window.showSaveFilePicker(opts);
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        return true;
      } catch (err) {
        // User cancelled or API error
        if (err.name === 'AbortError') {
          return false;
        }
        // Fall back to traditional download
        console.warn('File System Access API failed, falling back to download:', err);
      }
    }
    
    // Fallback to traditional download approach
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
    
    return true;
  } catch (error) {
    console.error('Error saving code to file:', error);
    return false;
  }
};

/**
 * Extract file extension from filename
 * 
 * @param {string} filename - The filename
 * @returns {string} - The extension
 */
const getExtensionFromFilename = (filename) => {
  if (!filename) return 'txt';
  
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : 'txt';
};

export default {
  saveCodeToFile,
  prepareCodeForSave
};